"use client";

import { cn } from "~/lib/utils";
import { useDiscount } from "~/hooks/use-discount";
import { useOrganisation } from "~/hooks/use-organisation";
import { EUR } from "~/lib/currency";
import { Badge } from "./badge";
import { Building2, Coffee } from "lucide-react";
import { IconTextRow } from "./icon-text-row";

const DAYS_BETWEEN_REFERENCE_SESSIONS = 14;
const MAX_COFFEE_COMPARISON_PER_DAY_EUR = 10;
const dailyEUR = new Intl.NumberFormat("sr-RS", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

interface PriceDisplayProps {
  originalPrice: number;
  className?: string;
  showPerSession?: boolean;
  showDailyBreakdown?: boolean;
  size?: "sm" | "md" | "lg";
  layout?: "horizontal" | "vertical";
  /** If true, checks org sponsorship and hides price if sponsored */
  checkOrgSponsorship?: boolean;
}

export const PriceDisplay = ({
  originalPrice,
  className,
  showPerSession = true,
  showDailyBreakdown = false,
  size = "md",
  layout = "vertical",
  checkOrgSponsorship = false,
}: PriceDisplayProps) => {
  const {
    hasDiscount,
    calculateDiscountedPrice,
    getDiscountAmount,
    getDiscountPercentageString,
    isFreeSession,
  } = useDiscount();

  const {
    shouldHidePrice,
    organisationName,
    isLoading: isLoadingOrg,
  } = useOrganisation();

  const discountedPrice = calculateDiscountedPrice(originalPrice);
  const discountAmount = getDiscountAmount(originalPrice);
  const dailyEquivalent = discountedPrice / DAYS_BETWEEN_REFERENCE_SESSIONS;

  const sizeClasses = {
    sm: {
      price: "text-lg font-semibold",
      originalPrice: "text-sm",
      badge: "text-xs",
      perSession: "text-xs",
      savings: "text-xs",
    },
    md: {
      price: "text-2xl font-semibold md:text-3xl",
      originalPrice: "text-lg",
      badge: "text-sm",
      perSession: "text-sm md:text-base",
      savings: "text-sm",
    },
    lg: {
      price: "text-3xl font-semibold md:text-4xl",
      originalPrice: "text-xl",
      badge: "text-base",
      perSession: "text-base md:text-lg",
      savings: "text-sm",
    },
  };

  const classes = sizeClasses[size];

  // Show organisation sponsored badge if user is org member with available sessions
  if (checkOrgSponsorship && shouldHidePrice && !isLoadingOrg) {
    return (
      <div className={cn("flex flex-col items-center gap-2", className)}>
        <Badge
          variant="secondary"
          className={cn(classes.badge, "flex items-center gap-1.5")}
        >
          <Building2 className="h-3.5 w-3.5" />
          Pokriveno od strane {organisationName}
        </Badge>
        {showPerSession && (
          <span className={cn(classes.perSession, "text-muted-foreground")}>
            Bez dodatnih troškova
          </span>
        )}
      </div>
    );
  }

  if (isFreeSession()) {
    return (
      <div className={cn("debug flex flex-col items-center gap-2", className)}>
        <div className="flex items-center gap-2">
          <span className={cn(classes.price, "text-brand")}>BESPLATNO</span>
          <Badge variant="destructive" className={classes.badge}>
            100% popust!
          </Badge>
        </div>
        {hasDiscount && (
          <div className="text-center">
            <span
              className={cn(
                classes.originalPrice,
                "text-muted-foreground line-through",
              )}
            >
              {EUR.format(originalPrice)}
            </span>
          </div>
        )}
      </div>
    );
  }

  if (layout === "horizontal") {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {hasDiscount && (
          <span
            className={cn(
              classes.originalPrice,
              "text-muted-foreground line-through",
            )}
          >
            {EUR.format(originalPrice)}
          </span>
        )}
        <span
          className={cn(
            classes.price,
            hasDiscount ? "text-brand" : "text-primary",
          )}
        >
          {EUR.format(discountedPrice)}
        </span>
        {hasDiscount && (
          <Badge variant="secondary" className={classes.badge}>
            -{getDiscountPercentageString()}
          </Badge>
        )}
        {showPerSession && (
          <span className={cn(classes.perSession, "text-muted-foreground")}>
            / sesija
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-1 md:items-start",
        className,
      )}
    >
      {hasDiscount && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className={classes.badge}>
            🎉 {getDiscountPercentageString()} popust!
          </Badge>
        </div>
      )}

      <div className="flex flex-col items-center gap-1 md:flex-row md:items-baseline md:gap-3">
        {hasDiscount && (
          <span
            className={cn(
              classes.originalPrice,
              "text-muted-foreground line-through",
            )}
          >
            {EUR.format(originalPrice)}
          </span>
        )}
        <span
          className={cn(
            classes.price,
            hasDiscount ? "text-brand" : "text-primary",
          )}
        >
          {EUR.format(discountedPrice)}
        </span>
        {showPerSession && (
          <span className={cn(classes.perSession, "text-muted-foreground")}>
            po sesiji
          </span>
        )}
      </div>

      {hasDiscount && discountAmount > 0 && (
        <p className={cn(classes.savings, "text-brand font-medium")}>
          Ušteda: {EUR.format(discountAmount)}
        </p>
      )}

      {showDailyBreakdown && (
        <IconTextRow
          icon={
            dailyEquivalent <= MAX_COFFEE_COMPARISON_PER_DAY_EUR
              ? Coffee
              : undefined
          }
          className="mt-2 text-left"
        >
          <div>
            <p className="text-brand text-sm leading-snug">
              <span className="font-semibold">
                Oko {dailyEUR.format(dailyEquivalent)} dnevno
              </span>
              {dailyEquivalent <= MAX_COFFEE_COMPARISON_PER_DAY_EUR &&
                ", otprilike kao kafa u kafiću."}
            </p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Ako zakazuješ jednu sesiju na dve nedelje.
            </p>
          </div>
        </IconTextRow>
      )}
    </div>
  );
};
